import { defineComponent as _defineComponent } from 'vue'
import { BModal } from 'bootstrap-vue';
import {
  WalletFundingDetails,
  WalletFundingPaymentType,
  Wallet,
  BankingScheme,
  Client,
  FeePaymentType,
} from 'ah-api-gateways';
import WalletFundingDetailsViewer from './WalletFundingDetailsViewer.vue';
import { makeFormModel, setState, getChildModel } from 'ah-common-lib/src/form/helpers';
import { radioField } from 'ah-common-lib/src/form/models';
import { FieldOptionObj, FormDefinition } from 'ah-common-lib/src/form/interfaces';
import CurrencyFundingFee from 'ah-trades/src/components/fees/CurrencyFundingFee.vue';
import CurrencySelectInput from 'ah-trades/src/components/currency/CurrencySelectInput.vue';
import { ValidatedForm } from 'ah-common-lib/src/form/components';
import { computed, onBeforeMount, provide, reactive, ref, watch, set } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useAhWalletsState } from '..';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddMoneyModal',
  props: {
    buttonTitle: { default: 'Fund Account' },
    wallet: { default: null },
    noCurrencySelect: { type: [String, Boolean], default: false },
    currency: { default: '' },
    allowedCurrencies: null,
    clientId: null
  },
  setup(__props: any) {

const props = __props as { buttonTitle: string, wallet: Wallet | null, noCurrencySelect: string | boolean, currency: string, allowedCurrencies?: string[], clientId?: string };



const state = reactive<{
  fundingDetails: { [key: string]: WalletFundingDetails[] };
  selectedCurrency: string;
  onBehalfOfClient: Client | null;
}>({
  fundingDetails: {},
  selectedCurrency: '',
  onBehalfOfClient: null,
});

provide('onBehalfOfClient', state.onBehalfOfClient);

const fundingOptions: FieldOptionObj[] = [
  { label: 'Local funding', value: BankingScheme.LOCAL },
  { label: 'SWIFT funding', value: BankingScheme.SWIFT },
];

const fundingFormDef = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'fundingForm',
    state: {
      title: '',
      class: 'form-narrow margin-auto',
    },
    fieldType: 'form',
    fields: [
      radioField('funding', '', fundingOptions, {
        defaultValue: BankingScheme.LOCAL,
        inline: true,
        titleTooltip:
          'Regular funding is a regular local payment and intermediary bank charges will not apply. Priority payments are sent via the SWIFT network. When a payment is sent via SWIFT, intermediary bank charges may apply.',
      }),
    ],
  }),
  validation: null,
});

const modal = ref<InstanceType<typeof BModal> | null>(null);

const walletState = useAhWalletsState();

const onBehalfOfClient = useOnBehalfOf();

onBeforeMount(() => walletState.store.useSettingsStore().loadPaymentRails());

const requestManager = useRequestManager({
  exposeToParent: true,
  onRetryFromParentManager: (k: string) => {
    if (k.startsWith('getClient-')) {
      onClientChange();
    } else if (k === 'loadFundingDetails') {
      loadFundingDetails(props.currency);
    }
  },
});

const modalTitle = computed(() => `Add money to ${props.wallet ? `${props.wallet.currency} ` : ''}wallet`);

const priorityFundingDetails = computed(() =>
  selectedCurrencyDetails.value?.find((d) => d.paymentType === WalletFundingPaymentType.PRIORITY)
);

const bankingScheme = computed(() =>
  fundingFormDef.form.funding === 'SWIFT' ? BankingScheme.SWIFT : BankingScheme.LOCAL
);

const selectedCurrencyDetails = computed(() =>
  state.selectedCurrency ? state.fundingDetails[state.selectedCurrency] : undefined
);

const fundingDetail = computed(() =>
  selectedCurrencyDetails.value?.find((details) => details.paymentRail === fundingFormDef.form.funding)
);

const rail = computed(() => {
  return walletState.store.useSettingsStore().getPaymentRail({
    currency: state.selectedCurrency,
    bankingScheme: BankingScheme.LOCAL,
    type: FeePaymentType.RECEIPT,
  });
});

const regularFundingDetails = computed(() => {
  const fundingDetailsArray: WalletFundingDetails[] = [];
  selectedCurrencyDetails.value?.forEach((d) => {
    if (d.paymentType === WalletFundingPaymentType.REGULAR) {
      fundingDetailsArray.push(d);
    }
  });
  return fundingDetailsArray;
});

const currentClientId = computed(
  () => walletState.store.useAuthStore().loggedInIdentity!.client?.id ?? onBehalfOfClient.value?.id
);

function onClientChange() {
  if (props.clientId) {
    requestManager.manager
      .sameOrCancelAndNew(`getClient-${props.clientId}`, walletState.services.client.getClient(props.clientId))
      .subscribe((response) => (state.onBehalfOfClient = response));
  }
}

watch(
  () => [props.wallet, props.currency],
  () => {
    if (props.wallet) {
      state.selectedCurrency = props.wallet.currency;
    } else {
      state.selectedCurrency = props.currency;
    }
  },
  { immediate: true }
);

watch(() => props.clientId, onClientChange, { immediate: true });

watch(
  () => [state.fundingDetails, rail.value],
  () => {
    const model = getChildModel(fundingFormDef.form, 'funding')!;
    let options = [...fundingOptions];
    if (rail.value.length > 0) {
      options = rail.value.map((i) => ({ label: i.label + ' funding', value: i.paymentRail }));
      options[rail.value.length] = {
        label: 'SWIFT funding',
        value: BankingScheme.SWIFT,
      };
    }
    options = options.map((o: any) => {
      const out = { ...o };
      if (out.value === BankingScheme.SWIFT) out.disabled = !priorityFundingDetails.value;
      else out.disabled = regularFundingDetails.value.length === 0;
      return out;
    });

    setState(model, 'options', options);

    fundingFormDef.form.funding = BankingScheme.SWIFT;
  },
  { immediate: true }
);

function show() {
  modal.value?.show();
  if (state.selectedCurrency) {
    loadFundingDetails(state.selectedCurrency);
  }
}

function onCurrencyChanged(currency: string) {
  state.selectedCurrency = currency;
  loadFundingDetails(currency);
}

function loadFundingDetails(currency: string) {
  requestManager.manager
    .sameOrNew(
      `loadFundingDetails-currency`,
      walletState.services.wallet.getWalletFundingDetails(
        currency,
        props.clientId ?? currentClientId.value,
        onBehalfOfClient.value?.id
      )
    )
    .subscribe((details) => {
      set(state.fundingDetails, currency, details);
    });
}

return { __sfc: true,props, state, fundingOptions, fundingFormDef, modal, walletState, onBehalfOfClient, requestManager, modalTitle, priorityFundingDetails, bankingScheme, selectedCurrencyDetails, fundingDetail, rail, regularFundingDetails, currentClientId, onClientChange, show, onCurrencyChanged, loadFundingDetails, BModal, FeePaymentType, WalletFundingDetailsViewer, CurrencyFundingFee, CurrencySelectInput, ValidatedForm }
}

})